import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Box, Button, Card, Checkbox, Chip, CircularProgress, Divider, FormControlLabel, IconButton, Paper, Stack, Switch, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { createFileRoute, Link, Navigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ContentDocument } from '@/lib/types';

import { useNovel } from '@/context/novel-context';
import { usePublishChapter } from '@/features/chapters/hooks/use-publish-chapter';
import { generateDocx } from '@/features/export/docx-export';
import { useFirebase } from '@/firebase';
import { useNovelRepository } from '@/hooks/useNovelRepository';

type ExportMode = 'docx' | 'structured-docx' | 'markdown' | 'txt';

export const Route = createFileRoute('/export')({ component: ExportPage });

const htmlToText = (html: string) => {
  const document = new DOMParser().parseFromString(html || '', 'text/html');
  document.querySelectorAll('br').forEach(node => node.replaceWith('\n'));
  document.querySelectorAll('p,div,h1,h2,h3,h4,h5,h6,li,blockquote,hr').forEach(node => node.append('\n'));
  return (document.body.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
};

function ExportPage() {
  const { t } = useTranslation();
  const { user, isUserLoading } = useFirebase();
  const { novel, isLoading } = useNovel();
  const { contentDao } = useNovelRepository();
  const { assembleFinalEdition, isAssembling } = usePublishChapter();
  const chapters = useMemo(() => Object.values(novel.summaries.chapters).filter(chapter => !chapter.isArchive).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [novel.summaries.chapters]);
  const scenes = useMemo(() => Object.values(novel.summaries.scenes).filter(scene => !scene.isArchive).sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [novel.summaries.scenes]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<ExportMode>('structured-docx');
  const [includePageNumbers, setIncludePageNumbers] = useState(false);
  const [contentDocuments, setContentDocuments] = useState<Record<string, ContentDocument>>({});
  const [loadingContent, setLoadingContent] = useState(false);

  useEffect(() => setSelected(new Set(chapters.filter(chapter => chapter.mainContentId && ['preview', 'public'].includes(chapter.publishStatus || '')).map(chapter => chapter.id))), [novel.id]);
  useEffect(() => {
    let cancelled = false;
    const chosen = chapters.filter(chapter => selected.has(chapter.id) && chapter.mainContentId);
    if (!chosen.length) { setContentDocuments({}); return; }
    setLoadingContent(true);
    Promise.all(chosen.map(async chapter => [chapter.id, await contentDao.getContent<ContentDocument>(novel.ownerId, novel, chapter.mainContentId!)] as const))
      .then(entries => { if (!cancelled) setContentDocuments(Object.fromEntries(entries.filter((entry): entry is [string, ContentDocument] => Boolean(entry[1])))); })
      .finally(() => { if (!cancelled) setLoadingContent(false); });
    return () => { cancelled = true; };
  }, [selected, chapters, contentDao, novel]);

  if (isUserLoading || isLoading) return <Box sx={{ minHeight: 400, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  if (!user) return <Navigate to="/login" search={{}} />;
  if (!novel.id) return <Navigate to="/" />;

  const selectedChapterSummaries = chapters.filter(chapter => selected.has(chapter.id));
  const toggle = (id: string) => setSelected(previous => {
    const next = new Set(previous);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const download = () => {
    let blob: Blob;
    let extension: string;
    if (mode === 'docx' || mode === 'structured-docx') {
      blob = generateDocx(selectedChapterSummaries.map(chapter => ({
        id: chapter.id,
        title: chapter.name,
        html: contentDocuments[chapter.id]?.scratchpad || '',
        sceneIds: contentDocuments[chapter.id]?.sceneIds || [],
        scenes: scenes.filter(scene => scene.chapterId === chapter.id),
      })), { title: novel.title, language: novel.language, structured: mode === 'structured-docx', includePageNumbers }).blob;
      extension = 'docx';
    } else {
      const text = [mode === 'markdown' ? `# ${novel.title}` : novel.title.toUpperCase(), ...selectedChapterSummaries.flatMap(chapter => [mode === 'markdown' ? `## ${chapter.name}` : chapter.name.toUpperCase(), htmlToText(contentDocuments[chapter.id]?.scratchpad || '')])].join('\n\n');
      blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      extension = mode === 'markdown' ? 'md' : 'txt';
    }
    const url = URL.createObjectURL(blob), anchor = document.createElement('a');
    anchor.href = url; anchor.download = `${novel.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.${extension}`; anchor.click(); setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  const isDocx = mode === 'docx' || mode === 'structured-docx';
  return <Box sx={{ width: '100%', minWidth: 0, boxSizing: 'border-box', p: { xs: 1, md: 3 }, maxWidth: 1500, mx: 'auto' }}>
    <Stack direction="row" alignItems="center" spacing={1} mb={3}>
      <IconButton component={Link} to="/" aria-label={t('common.back')}><ArrowBackIcon /></IconButton>
      <Box>
        <Typography variant="h3">{t('export.title')}</Typography>
        <Typography color="text.secondary">{t('export.description')}</Typography>
      </Box>
    </Stack>
    <Card sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', lg: 'center' }}
        gap={2}
        sx={{ p: 2, pb: { xs: 1, lg: 0 } }}
      >
        <Box sx={{ width: { xs: '100%', lg: 520 }, maxWidth: '100%', flexShrink: 0 }}>
          <Typography variant="overline" color="text.secondary">{t('export.mode')}</Typography>
          <ToggleButtonGroup
            exclusive
            value={mode}
            onChange={(_, value) => value && setMode(value)}
            size="small"
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
              width: '100%',
              '& .MuiToggleButton-root': { width: '100%', minWidth: 0, m: 0 },
            }}
          >
            <ToggleButton value="docx" sx={{ whiteSpace: 'nowrap' }}>DOCX</ToggleButton>
            <ToggleButton value="structured-docx" sx={{ whiteSpace: 'nowrap' }}>{t('export.structured_docx')}</ToggleButton>
            <ToggleButton value="markdown" sx={{ whiteSpace: 'nowrap' }}>Markdown</ToggleButton>
            <ToggleButton value="txt" sx={{ whiteSpace: 'nowrap' }}>{t('export.plain_txt')}</ToggleButton>
          </ToggleButtonGroup>
          {/* TODO: add EPUB and RTF exporters. */}
        </Box>
        <Stack direction="row" alignItems="center" justifyContent={{ xs: 'flex-end', lg: 'initial' }} spacing={1}>
          <Button onClick={() => setSelected(new Set(chapters.filter(chapter => chapter.mainContentId).map(chapter => chapter.id)))}>
            {t('export.select_all_exportable')}
          </Button>
          <Button variant="contained" startIcon={<FileDownloadOutlinedIcon />} disabled={!selected.size || loadingContent} onClick={download}>
            {t('export.export_chapters', { count: selected.size })}
          </Button>
        </Stack>
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(380px,.9fr) minmax(440px,1.1fr)' }, gap: 3 }}>
        <FormControlLabel
          sx={{
            gridColumn: { xs: '1', lg: '2' },
            m: 0,
            px: { xs: 2, lg: 0 },
            pb: 2,
            pt: { xs: 1, lg: 0.5 },
            visibility: isDocx ? 'visible' : 'hidden',
            justifySelf: 'start',
          }}
          control={<Switch size="small" checked={includePageNumbers} onChange={event => setIncludePageNumbers(event.target.checked)} />}
          label={<Typography variant="caption">{t('export.include_page_numbers')}</Typography>}
        />
      </Box>
    </Card>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(380px,.9fr) minmax(440px,1.1fr)' }, gap: 3 }}>
      <Stack spacing={1.5}>
        {chapters.map(chapter => {
          const assembled = Boolean(chapter.mainContentId);
          const published = ['preview', 'public'].includes(chapter.publishStatus || '');
          const publishStatus = chapter.publishStatus || 'draft';
          return (
            <Paper key={chapter.id} variant="outlined" sx={{ p: 2, opacity: assembled ? 1 : .72 }}>
              <Stack direction="row" alignItems="center" gap={1.5}>
                <Tooltip title={assembled ? '' : t('export.no_assembled_tooltip')}>
                  <span><Checkbox checked={selected.has(chapter.id)} disabled={!assembled} onChange={() => toggle(chapter.id)} /></span>
                </Tooltip>
                <Box flex={1} minWidth={0}>
                  <Typography fontWeight={650}>{chapter.name}</Typography>
                  <Stack direction="row" gap={1} mt={.5}>
                    <Chip
                      size="small"
                      label={t('export.status.' + publishStatus)}
                      color={published ? 'primary' : 'default'}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      icon={assembled ? <CheckCircleOutlineIcon /> : <LockOutlinedIcon />}
                      label={assembled ? t('export.assembled') : t('export.not_assembled')}
                    />
                  </Stack>
                </Box>
                {!assembled && (
                  <Tooltip title={t('export.blind_assemble_tooltip')}>
                    <Button size="small" variant="outlined" disabled={isAssembling} onClick={() => assembleFinalEdition(chapter.id, true, false)}>
                      {t('export.blind_assemble')}
                    </Button>
                  </Tooltip>
                )}
              </Stack>
            </Paper>
          );
        })}
      </Stack>
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, minHeight: 520, position: { lg: 'sticky' }, top: 24, alignSelf: 'start' }}>
        <Stack direction="row" alignItems="center" gap={1} mb={2}>
          <VisibilityOutlinedIcon color="primary" />
          <Typography variant="h5">{t('export.preview')}</Typography>
        </Stack>
        <Divider sx={{ mb: 3 }} />
        <Typography variant="h3" sx={{ fontFamily: 'serif', mb: 3 }}>{novel.title}</Typography>
        {!selectedChapterSummaries.length && (
          <Typography color="text.secondary">{t('export.preview_empty')}</Typography>
        )}
        {selectedChapterSummaries.map(chapter => (
          <Box key={chapter.id} mb={3}>
            <Typography variant="h5" fontWeight={700}>{chapter.name}</Typography>
            {mode === 'structured-docx' ? (
              <Stack pl={2} mt={1} spacing={.5}>
                {scenes.filter(scene => scene.chapterId === chapter.id).map(scene => (
                  <Typography key={scene.id} color="text.secondary">
                    {t('export.scene_name', { name: scene.name })}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography color="text.secondary" mt={1} whiteSpace="pre-line" sx={{ display: '-webkit-box', WebkitLineClamp: 6, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {htmlToText(contentDocuments[chapter.id]?.scratchpad || '') || (loadingContent ? t('export.loading_content') : t('export.no_content'))}
              </Typography>
            )}
            <Divider sx={{ mt: 2 }} />
          </Box>
        ))}
      </Paper>
    </Box>
  </Box>;
}
